import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearDataServers, getDataServers, selectDataServers } from "@/store/serverSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SERVER } from "@/constants/accessRights";
import SideTab from "@/HOC/SideTab/SideTab";
import { IServerStatusProps } from "./ServerStatus.props";
import styles from "./ServerStatus.module.scss";

const ServerStatus = ({ showServers, setShowServers, serviceType }: IServerStatusProps): JSX.Element => {
	const [failServers, setFailServers] = useState<string[]>([]); // список сбойных серверов
	const [showWebsocketServers, setShowWebsocketServers] = useState<boolean>(false); // показ вебсокет-серверов

	const dispatch = useAppDispatch();
	const dataServers = useAppSelector(selectDataServers); // store - данные о серверах

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	useEffect(() => {
		isAccess(SERVER.ADDRESSES) && dispatch(getDataServers({ serviceType })); // получаем данные о серверах

		// автозапрос списка серверов каждые 30 сек
		const interval = setInterval(() => {
			isAccess(SERVER.ADDRESSES) && dispatch(getDataServers({ serviceType }));
		}, 30000);

		// при уходе со страницы
		return () => {
			clearInterval(interval); // удаляем автозапрос получения данных о серверах
			dispatch(clearDataServers()); // очищаем данные по серверам
		};
	}, []);

	// следим за открытие вкладки
	useEffect(() => {
		if (showServers) document.addEventListener("keydown", keyDownHandler); // вешаем обработчик
		return () => {
			document.removeEventListener("keydown", keyDownHandler); // удаляем обработчик
		};
	}, [showServers]);

	// следим за данными о серверах
	useEffect(() => {
		dataServers && dataServers.fail && setFailServers(Object.keys(dataServers.fail)); // если есть данные - записываем в state сбойные серверы
	}, [dataServers]);

	// обработчик нажатия клавиш
	const keyDownHandler = (e: KeyboardEvent): void => {
		if (e.ctrlKey && e.altKey && e.code === "KeyW") setShowWebsocketServers((prev) => !prev); // вкл/выкл отображение вебсокет-серверов
	};

	// функция получения локального времени сбоя сервера
	const getLocalServerCrashTime = (serverAddress: string): string => {
		if (dataServers) {
			// секунды переводим в милисекунды
			const date = new Date(+dataServers?.fail[serverAddress] * 1000).toLocaleString();
			return `${translate("title_failureTime")}: ${date}`;
		}
		return "";
	};

	return (
		<SideTab showTab={showServers} setShowTab={setShowServers} width={240} height="100%">
			<div className={styles.server}>
				<h3 className={styles.serverTitle}>
					<span
						className={
							failServers.length || !dataServers || typeof dataServers === "string" || !Object.keys(dataServers).length ? styles.serverStatusFailed : styles.serverStatus
						}
					></span>
					{translate("title_servers")}
				</h3>

				{/* обучение */}
				<h4 className={styles.serverTrainerTitle}>{translate(serviceType === "res" ? "title_processing" : "title_training")}:</h4>
				<div className={styles.serverItem}>
					{dataServers?.trainer && <span className={failServers.includes(dataServers?.trainer) ? styles.serverStatusFailed : styles.serverStatus}></span>}
					{dataServers?.trainer ? (
						<div className={styles.serverLinkWrapper}>
							<a className={styles.serverLink} href={dataServers?.trainer} target="_blank" rel="noreferrer">
								{dataServers?.trainer}
							</a>
							{dataServers.versions[dataServers?.trainer] && <span className={styles.serverVersion}>{dataServers.versions[dataServers?.trainer]}</span>}
						</div>
					) : (
						translate("title_notFound").toLowerCase()
					)}
				</div>
				{failServers.length > 0 && dataServers?.trainer && failServers.includes(dataServers?.trainer) && (
					<p className={styles.serverFailedTime}>{getLocalServerCrashTime(dataServers?.trainer)}</p>
				)}

				{/* обработка */}
				<h4 className={styles.serverClusterTitle}>{translate(serviceType === "res" ? "title_reporting" : "title_processing")}:</h4>
				<ul className={styles.serverListCluster}>
					{dataServers?.cluster && Array.isArray(dataServers.cluster) ? (
						dataServers.cluster.map((server) => (
							<li key={server}>
								<div className={styles.serverItem}>
									<span className={failServers.includes(server) ? styles.serverStatusFailed : styles.serverStatus}></span>
									<div className={styles.serverLinkWrapper}>
										<a className={styles.serverLink} href={server} target="_blank" rel="noreferrer">
											{server}
										</a>
										{dataServers.versions[server] && <span className={styles.serverVersion}>{dataServers.versions[server]}</span>}
									</div>
								</div>
								{failServers.length > 0 && failServers.includes(server) && <p className={styles.serverFailedTime}>{getLocalServerCrashTime(server)}</p>}
							</li>
						))
					) : (
						<span className={styles.serverItem}>{translate("title_notFound").toLowerCase()}</span>
					)}
				</ul>

				{showWebsocketServers && (
					<>
						<h4 className={styles.serverWebsocketTitle}>{translate("title_websocket")}:</h4>
						<ul className={styles.serverListWebsocket}>
							{dataServers?.websockets && Array.isArray(dataServers.websockets) ? (
								dataServers?.websockets.map((server) => (
									<li key={server}>
										<div className={styles.serverItem}>
											<span className={failServers.includes(server) ? styles.serverStatusFailed : styles.serverStatus}></span>
											{server}
										</div>
										{failServers.length > 0 && failServers.includes(server) && <p className={styles.serverFailedTime}>{getLocalServerCrashTime(server)}</p>}
									</li>
								))
							) : (
								<span className={styles.serverItem}>{translate("title_notFound").toLowerCase()}</span>
							)}
						</ul>
						<h4 className={styles.serverWebsocketTitle}>
							{translate("title_websocket")} ({translate("title_assistant").toLowerCase()}):
						</h4>
						<ul className={styles.serverListWebsocket}>
							{dataServers?.assistants && Array.isArray(dataServers.assistants) ? (
								dataServers?.assistants.map((server) => (
									<li key={server}>
										<div className={styles.serverItem}>
											<span className={failServers.includes(server) ? styles.serverStatusFailed : styles.serverStatus}></span>
											{server}
										</div>
										{failServers.length > 0 && failServers.includes(server) && <p className={styles.serverFailedTime}>{getLocalServerCrashTime(server)}</p>}
									</li>
								))
							) : (
								<span className={styles.serverItem}>{translate("title_notFound").toLowerCase()}</span>
							)}
						</ul>
					</>
				)}
			</div>
		</SideTab>
	);
};

export default ServerStatus;
